import { Flex, Image, Text } from "@chakra-ui/react";
import { t } from "i18next";
import { useState } from "react";
import { FunnelComponentProps } from "../../funnel/types";
import { StripePaymentConfigTypes } from "./StripePayment";
import { usePaymentMutation } from "../hooks/usePaymentMutation";
import {
  purchasePod,
  ShippingAddress,
  TaxShippingResponse,
} from "../api/paymentApi";
import axios from "axios";
import { ServerResponse } from "../../../types";
import { FunnelSteps } from "../../funnel/FunnelSteps";
import { AddressStep } from "./AddressStep";
import { PaymentStep } from "./PaymentStep";
import { ConfirmationStep } from "./ConfirmationStep";

export interface TaxShippingData {
  shipping: number;
  taxes: number;
  proof: string;
  total: number;
}

interface PurchasePodModalBodyProps extends FunnelComponentProps {
  productId?: number;
  productName?: string;
  productPrice?: number;
  productImage?: string;
  comicName?: string;
}

export function PurchasePodModalBody({
  onClose,
  release,
}: PurchasePodModalBodyProps) {
  const { pod_price, pod_product_id, pod_product_name } = release || {};

  const [step, setStep] = useState(1);
  const [address, setAddress] = useState<ShippingAddress>({
    first_name: "",
    last_name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip: "",
    country_id: "us",
    phone_number: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveAddress, setSaveAddress] = useState(false);
  const [taxShippingData, setTaxShippingData] =
    useState<TaxShippingData | null>(null);

  const validateShippingAddress = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!address.first_name.trim()) {
      newErrors.first_name = t("components.payments.pod.errors.firstName");
    }
    if (!address.last_name.trim()) {
      newErrors.last_name = t("components.payments.pod.errors.lastName");
    }
    if (!address.address_line1.trim()) {
      newErrors.address_line1 = t("components.payments.pod.errors.address");
    }
    if (!address.city.trim()) {
      newErrors.city = t("components.payments.pod.errors.city");
    }
    if (!address.zip.trim()) {
      newErrors.zip = t("components.payments.pod.errors.zip");
    }
    if (!address.country_id) {
      newErrors.country_id = t("components.payments.pod.errors.country");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleTaxShippingSuccess = (data: TaxShippingResponse) => {
    const total = (pod_price || 0) + data.shipping + data.taxes;
    setTaxShippingData({
      shipping: data.shipping,
      taxes: data.taxes,
      proof: data.proof,
      total,
    });
    setStep(2);
  };

  const purchaseSuccess = async () => {
    setStep(3);
  };

  const mutationFn = (stripePaymentConfig: StripePaymentConfigTypes) => {
    if (!pod_product_id || !pod_price || !taxShippingData)
      return Promise.resolve();
    return purchasePod({
      product_id: pod_product_id,
      price: pod_price, // Must match the price in the proof token; backend calculates total
      stripe_token: stripePaymentConfig.stripeToken,
      owner_payment_service_token_id:
        stripePaymentConfig.ownerPaymentServiceTokenId
          ? parseInt(stripePaymentConfig.ownerPaymentServiceTokenId)
          : undefined,
      ...address,
      save_address: saveAddress,
      taxes_shipping: taxShippingData.proof,
    });
  };

  const onError = (error: Error) => {
    let errorMessage = t("components.payments.pod.errors.purchaseFailed");
    if (axios.isAxiosError<ServerResponse<string>>(error)) {
      errorMessage = error.response?.data?.payload.results || errorMessage;
    }
    return errorMessage;
  };

  const {
    mutate: completePurchase,
    failedCard,
    cardValidationError,
    waiting,
  } = usePaymentMutation(mutationFn, purchaseSuccess, onError);

  // Get step-specific text
  const getStepText = () => {
    switch (step) {
      case 1:
        return t("components.payments.pod.shippingAddress");
      case 2:
        return t("components.payments.pod.payment");
      case 3:
        return t("components.payments.pod.confirmation");
      default:
        return "";
    }
  };

  // Render step-specific content
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <AddressStep
            address={address}
            setAddress={setAddress}
            saveAddress={saveAddress}
            setSaveAddress={setSaveAddress}
            errors={errors}
            setErrors={setErrors}
            validateShippingAddress={validateShippingAddress}
            productId={pod_product_id}
            productPrice={pod_price}
            onTaxShippingSuccess={handleTaxShippingSuccess}
          />
        );

      case 2:
        return (
          <PaymentStep
            waiting={waiting}
            completePurchase={completePurchase}
            submitButtonText={`${t("components.payments.pod.completePurchase")} $${taxShippingData?.total?.toFixed(2) || pod_price?.toFixed(2) || "0.00"}`}
            cardValidationError={cardValidationError}
            failedCard={failedCard}
            onEditAddress={() => setStep(1)}
            productPrice={pod_price}
            taxShippingData={taxShippingData}
          />
        );

      case 3:
        return (
          <ConfirmationStep
            address={address}
            onClose={onClose || (() => {})}
            releaseTitle={release?.title}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Flex flexDirection="column" gap="24px" width="100%" p="24px">
      {/* Always visible: Product Preview (hidden on confirmation step) */}
      {step !== 3 && (
        <Flex flexDirection="column" gap="24px" width="100%">
          <ReleasePrintOnDemandInfo
            releaseImageUrl={release?.thumbnail_url}
            pod_product_name={pod_product_name}
            pod_price={pod_price}
            releaseTitle={release?.title}
            numberOfPages={release?.page_count}
          />

          {/* Always visible: FunnelSteps */}
          <FunnelSteps
            stepCount={3}
            currentStep={step}
            text={getStepText()}
            marginBottom="0"
            gap="12px"
          />
        </Flex>
      )}

      {/* Step-specific content */}
      {renderStepContent()}
    </Flex>
  );
}

interface ReleasePrintOnDemandInfoProps {
  releaseImageUrl?: string;
  pod_product_name?: string;
  pod_price?: number;
  releaseTitle?: string;
  numberOfPages?: number;
}

export function ReleasePrintOnDemandInfo({
  releaseImageUrl,
  pod_product_name,
  pod_price,
  releaseTitle,
  numberOfPages,
}: ReleasePrintOnDemandInfoProps) {
  return (
    <Flex gap="16px" alignItems="center">
      {releaseImageUrl && (
        <Image
          src={releaseImageUrl}
          alt={releaseTitle}
          borderRadius="8px"
          height="90px"
          aspectRatio={0.645}
        />
      )}
      <Flex flexDirection="column" gap="4px">
        <Text fontWeight="600" fontSize="16px">
          {pod_product_name}
        </Text>
        <Text variant="modalBody" fontSize="14px" color="neutral.400" mt="-2px">
          {numberOfPages} pages
        </Text>
        <Text fontWeight="700" fontSize="18px" color="blaze.blaze">
          ${pod_price?.toFixed(2)}
        </Text>
      </Flex>
    </Flex>
  );
}
